import * as React from "react";

import { cn } from "~/lib/utils";

// AIDEV-NOTE: ProgressRing — first circular progress primitive in the DS.
// Distilled from the onboarding checklist design (claude.ai/design, see
// docs/specs/practitioner-onboarding-checklist.md). Ring only; RingMeter
// overlays the "value/max" count in the center.

export interface ProgressRingProps
    extends Omit<React.SVGAttributes<SVGSVGElement>, "children"> {
    value: number;
    max: number;
    /** Outer size in px. */
    size?: number;
    /** Stroke width in px. */
    strokeWidth?: number;
    /** Tailwind stroke-* class for the track circle. */
    trackClassName?: string;
    /** Tailwind stroke-* class for the filled arc. */
    fillClassName?: string;
}

const ProgressRing = React.forwardRef<SVGSVGElement, ProgressRingProps>(
    (
        {
            value,
            max,
            size = 30,
            strokeWidth = 3,
            trackClassName = "stroke-brand-forest-100",
            fillClassName = "stroke-brand-forest-800",
            className,
            style,
            ...props
        },
        ref,
    ) => {
        const r = (size - strokeWidth - 1) / 2;
        const circumference = 2 * Math.PI * r;
        const pct = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;
        return (
            <svg
                ref={ref}
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                role="progressbar"
                aria-valuenow={value}
                aria-valuemin={0}
                aria-valuemax={max}
                className={cn("-rotate-90", className)}
                style={style}
                {...props}
            >
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    strokeWidth={strokeWidth}
                    className={trackClassName}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - pct)}
                    className={cn(
                        "transition-[stroke-dashoffset] duration-[620ms] ease-[cubic-bezier(.22,1,.36,1)]",
                        fillClassName,
                    )}
                />
            </svg>
        );
    },
);
ProgressRing.displayName = "ProgressRing";

export interface RingMeterProps extends React.HTMLAttributes<HTMLDivElement> {
    value: number;
    max: number;
    size?: number;
    /** Font size in px for the centered count. */
    fontSize?: number;
}

/** ProgressRing with the "value/max" count centered inside it. */
const RingMeter = React.forwardRef<HTMLDivElement, RingMeterProps>(
    ({ value, max, size = 42, fontSize = 11, className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                "relative flex shrink-0 items-center justify-center",
                className,
            )}
            style={{ width: size, height: size }}
            {...props}
        >
            <ProgressRing value={value} max={max} size={size} />
            <span
                className="absolute inset-0 flex items-center justify-center font-body font-bold text-brand-forest-800"
                style={{ fontSize }}
            >
                {value}/{max}
            </span>
        </div>
    ),
);
RingMeter.displayName = "RingMeter";

export { ProgressRing, RingMeter };
